import React, { useState, useEffect } from 'react';
import { Megaphone, Plus, Trash2, Save, ToggleLeft, ToggleRight, Eye, MousePointer, Code, AlertCircle } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const AD_TYPES = [
  { value: 'banner', label: 'Banner (728x90)' },
  { value: 'sidebar', label: 'Sidebar (300x250)' },
  { value: 'in_feed', label: 'In-Feed (responsive)' },
  { value: 'between_panels', label: 'Between Panels' },
  { value: 'chapter_end', label: 'Chapter End' },
];

const PLACEMENTS = [
  { value: 'header', label: 'Header' },
  { value: 'footer', label: 'Footer' },
  { value: 'sidebar', label: 'Sidebar' },
  { value: 'in_content', label: 'In Content' },
  { value: 'between_episodes', label: 'Between Episodes' },
  { value: 'homepage', label: 'Homepage' },
  { value: 'browse_page', label: 'Browse Page' },
  { value: 'comic_detail', label: 'Comic Detail Page' },
  { value: 'reader', label: 'Reader Page' },
];

export function AdminAds() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newAd, setNewAd] = useState({
    name: '',
    ad_type: 'banner',
    ad_code: '',
    placement: 'in_content',
    is_active: true,
    priority: 0
  });

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      const res = await api.get('/admin/ads');
      setAds(res.data);
    } catch (error) {
      toast.error('Failed to load ads');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAd = async () => {
    if (!newAd.name || !newAd.ad_code) {
      toast.error('Name and ad code are required');
      return;
    }

    setSaving(true);
    try {
      const res = await api.post('/admin/ads', newAd);
      setAds([res.data.ad, ...ads]);
      setShowNewForm(false);
      setNewAd({
        name: '',
        ad_type: 'banner',
        ad_code: '',
        placement: 'in_content',
        is_active: true,
        priority: 0
      });
      toast.success('Ad code created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ad');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAd = async (adId) => {
    try {
      const res = await api.post(`/admin/ads/${adId}/toggle`);
      setAds(ads.map(ad => 
        ad.id === adId ? { ...ad, is_active: res.data.is_active } : ad
      ));
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Failed to toggle ad');
    }
  };

  const handleDeleteAd = async (adId, adName) => {
    if (!window.confirm(`Delete ad "${adName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/admin/ads/${adId}`);
      setAds(ads.filter(ad => ad.id !== adId));
      toast.success('Ad deleted');
    } catch (error) {
      toast.error('Failed to delete ad');
    }
  };

  const handleUpdateAd = async (adId, field, value) => {
    try {
      const res = await api.put(`/admin/ads/${adId}`, { [field]: value });
      setAds(ads.map(ad => ad.id === adId ? res.data : ad));
      toast.success('Ad updated');
    } catch (error) {
      toast.error('Failed to update ad');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="text-[var(--primary)]" size={24} />
          <h2 className="text-2xl font-teko">Google Ads Management</h2>
        </div>
        <button
          onClick={() => setShowNewForm(!showNewForm)}
          className="btn-hotrod px-4 py-2 flex items-center gap-2"
        >
          <Plus size={18} />
          Add Ad Code
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="text-sm text-blue-400 font-medium">Google AdSense Integration</p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            Paste your Google AdSense ad unit code here. The code will be rendered on the website based on placement settings.
            Make sure your AdSense account is approved and ad units are created in the AdSense dashboard.
          </p>
        </div>
      </div>

      {/* New Ad Form */}
      {showNewForm && (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="font-teko text-xl mb-4">Create New Ad Code</h3>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">Ad Name</label>
              <input
                type="text"
                value={newAd.name}
                onChange={(e) => setNewAd({ ...newAd, name: e.target.value })}
                placeholder="e.g., Homepage Banner"
                className="w-full px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">Priority (higher = shown first)</label>
              <input
                type="number"
                value={newAd.priority}
                onChange={(e) => setNewAd({ ...newAd, priority: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">Ad Type</label>
              <select
                value={newAd.ad_type}
                onChange={(e) => setNewAd({ ...newAd, ad_type: e.target.value })}
                className="w-full px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
              >
                {AD_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[var(--text-secondary)] mb-1">Placement</label>
              <select
                value={newAd.placement}
                onChange={(e) => setNewAd({ ...newAd, placement: e.target.value })}
                className="w-full px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
              >
                {PLACEMENTS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-xs text-[var(--text-secondary)] mb-1">
              <Code size={14} className="inline mr-1" />
              Google AdSense Code
            </label>
            <textarea
              value={newAd.ad_code}
              onChange={(e) => setNewAd({ ...newAd, ad_code: e.target.value })}
              placeholder='<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-XXXXXX" crossorigin="anonymous"></script>
<ins class="adsbygoogle" style="display:block" data-ad-client="ca-pub-XXXXXX" data-ad-slot="XXXXXX" data-ad-format="auto"></ins>
<script>(adsbygoogle = window.adsbygoogle || []).push({});</script>'
              rows={6}
              className="w-full px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg font-mono text-xs"
            />
          </div>
          
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newAd.is_active}
                onChange={(e) => setNewAd({ ...newAd, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              Active immediately
            </label>
            <div className="flex-1" />
            <button
              onClick={() => setShowNewForm(false)}
              className="px-4 py-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateAd}
              disabled={saving}
              className="btn-hotrod px-6 py-2 flex items-center gap-2"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save size={16} />
              )}
              Create Ad
            </button>
          </div>
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-lg p-4">
          <p className="text-xs text-[var(--text-secondary)]">Total Ads</p>
          <p className="text-2xl font-teko font-bold">{ads.length}</p>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-lg p-4">
          <p className="text-xs text-[var(--text-secondary)]">Active</p>
          <p className="text-2xl font-teko font-bold text-green-500">{ads.filter(a => a.is_active).length}</p>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-lg p-4">
          <p className="text-xs text-[var(--text-secondary)]">Total Impressions</p>
          <p className="text-2xl font-teko font-bold">{ads.reduce((sum, a) => sum + (a.impressions || 0), 0).toLocaleString()}</p>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-lg p-4">
          <p className="text-xs text-[var(--text-secondary)]">Total Clicks</p>
          <p className="text-2xl font-teko font-bold">{ads.reduce((sum, a) => sum + (a.clicks || 0), 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Ads List */}
      {ads.length === 0 ? (
        <div className="text-center py-12 bg-[var(--surface)] border border-[var(--border)] rounded-xl">
          <Megaphone className="mx-auto text-[var(--text-secondary)] mb-4" size={48} />
          <p className="text-[var(--text-secondary)]">No ad codes configured</p>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Click "Add Ad Code" to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {ads.map((ad) => (
            <div 
              key={ad.id} 
              className={`bg-[var(--surface)] border rounded-xl p-5 ${
                ad.is_active ? 'border-[var(--border)]' : 'border-yellow-500/50 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleToggleAd(ad.id)}
                    className={`transition-colors ${ad.is_active ? 'text-green-500' : 'text-[var(--text-secondary)]'}`}
                    title={ad.is_active ? 'Click to disable' : 'Click to enable'}
                  >
                    {ad.is_active ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                  </button>
                  <div>
                    <h3 className="font-medium">{ad.name}</h3>
                    <p className="text-xs text-[var(--text-secondary)]">
                      {AD_TYPES.find(t => t.value === ad.ad_type)?.label || ad.ad_type} • {PLACEMENTS.find(p => p.value === ad.placement)?.label || ad.placement}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteAd(ad.id, ad.name)}
                  className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4 mb-3">
                <div className="flex items-center gap-2 text-sm">
                  <Eye size={16} className="text-[var(--text-secondary)]" />
                  <span>{(ad.impressions || 0).toLocaleString()} impressions</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MousePointer size={16} className="text-[var(--text-secondary)]" />
                  <span>{(ad.clicks || 0).toLocaleString()} clicks</span>
                </div>
                <div className="text-sm text-[var(--text-secondary)]">
                  CTR: {ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : 0}%
                </div>
              </div>
              
              <details className="group">
                <summary className="cursor-pointer text-xs text-[var(--text-secondary)] hover:text-[var(--primary)] flex items-center gap-1">
                  <Code size={12} />
                  View/Edit Ad Code
                </summary>
                <textarea
                  defaultValue={ad.ad_code}
                  onBlur={(e) => {
                    if (e.target.value !== ad.ad_code) {
                      handleUpdateAd(ad.id, 'ad_code', e.target.value);
                    }
                  }}
                  rows={4}
                  className="w-full mt-2 px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg font-mono text-xs"
                />
              </details>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminAds;
